// SchedulerViewHeaderPhoto.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './SchedulerViewHeaderPhoto.css';

/**
 * SchedulerViewHeaderPhoto
 *
 * Renders a node cover photo for scheduler column headers without any initials fallback.
 *
 * @param {object} node - The node represented by the scheduler column header.
 * @param {number} size - The rendered square size in pixels.
 * @param {string|number} borderRadius - The border radius applied to the container and image.
 *
 * @returns {ReactNode} The rendered header photo, or null when no cover photo is available.
 */
const SchedulerViewHeaderPhoto = ({
    node,
    size = 22,
    borderRadius = '999px',
}) => {

    // ----- STATE ----- //

    const [resolvedCoverUrl, setResolvedCoverUrl] = useState('');
    const [imageFailed, setImageFailed] = useState(false);

    // ----- MEMOS ----- //

    const rawCoverUrl = useMemo(() => (
        typeof node?.coverUrl === 'string' ? node.coverUrl.trim() : ''
    ), [node?.coverUrl]);

    const coverStoragePath = useMemo(() => {
        if (typeof node?.coverStoragePath === 'string' && node.coverStoragePath.trim()) {
            return node.coverStoragePath.trim();
        }

        if (typeof node?.storagePath === 'string' && node.storagePath.trim()) {
            return node.storagePath.trim();
        }

        return '';
    }, [node?.coverStoragePath, node?.storagePath]);

    const canRenderRawCoverUrl = useMemo(() => (
        rawCoverUrl.startsWith('blob:')
        || rawCoverUrl.startsWith('data:')
        || rawCoverUrl.includes('://')
    ), [rawCoverUrl]);

    const imageSrc = resolvedCoverUrl || (canRenderRawCoverUrl ? rawCoverUrl : '');
    const showImage = Boolean(imageSrc) && !imageFailed;

    // ----- EFFECTS ----- //

    useEffect(() => {
        let cancelled = false;

        setImageFailed(false);

        if (!rawCoverUrl && !coverStoragePath) {
            setResolvedCoverUrl('');
            return undefined;
        }

        if (rawCoverUrl.startsWith('blob:') || rawCoverUrl.startsWith('data:')) {
            setResolvedCoverUrl(rawCoverUrl);
            return undefined;
        }

        (async () => {
            try {
                const url = await resolveImageUrl({
                    nodeKey: node?.key || null,
                    storagePath: coverStoragePath || null,
                    url: rawCoverUrl || null,
                });

                if (!cancelled) {
                    setResolvedCoverUrl(url || (canRenderRawCoverUrl ? rawCoverUrl : ''));
                }
            } catch (error) {
                if (!cancelled) {
                    setResolvedCoverUrl(canRenderRawCoverUrl ? rawCoverUrl : '');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [canRenderRawCoverUrl, coverStoragePath, node?.key, rawCoverUrl]);

    useEffect(() => {
        if (resolvedCoverUrl) {
            setImageFailed(false);
        }
    }, [resolvedCoverUrl]);

    if (!node || !showImage) return null;

    // ----- RENDER ----- //

    return (
        <div
          className="scheduler-view-header-photo-container"
          style={{
                height: `${size}px`,
                width: `${size}px`,
            }}
        >
            {/* IMAGE */}
            <img
              className="scheduler-view-header-photo-image"
              style={{
                    borderRadius,
                }}
              alt={String(node?.title || '').trim() || 'Scheduler resource'}
              src={imageSrc}
              onError={() => setImageFailed(true)}
            />
        </div>
    );
};

// Dev Tools
SchedulerViewHeaderPhoto.displayName = 'SchedulerViewHeaderPhoto';

export default React.memo(SchedulerViewHeaderPhoto);
